export function filterAllowedProducts(productTypes, productMap) {
  return productTypes
    .filter((type) => productMap[type])
    .map((type) => ({ key: type, ...productMap[type] }));
}

const DEFAULT_JUNIOR_WEIGHT = '310g';

export function buildCartProperties(formEntries, lockedOptions, teamSlug, productType) {
  const properties = {};

  for (const [key, value] of formEntries) {
    if (key.startsWith('properties[') && key.endsWith(']')) {
      const propName = key.slice('properties['.length, -1);
      properties[propName] = value;
    }
  }

  for (const [key, values] of Object.entries(lockedOptions)) {
    if (key === 'weight') continue;
    if (values.length !== 1) continue;
    const titleCased = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    properties[titleCased] = values[0];
  }

  for (const key of ['Weight']) {
    if (properties[key]) {
      properties[`_${key}`] = properties[key];
      delete properties[key];
    }
  }

  const weightValues = resolveWeightForProduct(lockedOptions.weight, productType);
  if (weightValues.length === 1) {
    properties._Weight = weightValues[0];
  }

  properties._team = teamSlug;

  return properties;
}

export function resolveWeightForProduct(weightOption, productType) {
  if (!weightOption || typeof weightOption !== 'object') {
    return productType === 'junior' ? [DEFAULT_JUNIOR_WEIGHT] : [];
  }
  const values = weightOption[productType];
  if (values) return values;
  return productType === 'junior' ? [DEFAULT_JUNIOR_WEIGHT] : [];
}

export function findVariantByTeamSlug(options, variants, teamSlug) {
  const teamOption = options.find((option) => option.name === 'Team');
  if (!teamOption) return variants[0];
  const field = `option${teamOption.position}`;
  return variants.find((variant) => variant[field] === teamSlug) || variants[0];
}

export function buildProductFormHtml(variant, { teamSlug = '', teamUrl = '' } = {}) {
  return `
    <form action="/cart/add" method="post">
      <input type="hidden" name="id" value="${variant.id}">
      <input type="hidden" name="properties[_stick_image]" value="">
      <input type="hidden" name="properties[_team_url]" value="${teamUrl}">
      <input type="hidden" name="properties[_team]" value="${teamSlug}">
      <div class="team-page__quantity quantity-selector">
        <button class="button quantity-minus button-unstyled" type="button" name="minus" aria-label="Decrease quantity">
          <span class="svg-wrapper icon-plus"><svg width="16" height="2" viewBox="0 0 16 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>
        </button>
        <input type="number" name="quantity" value="1" min="1" aria-label="Quantity">
        <button class="button quantity-plus button-unstyled" type="button" name="plus" aria-label="Increase quantity">
          <span class="svg-wrapper icon-plus"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 1V15M1 8H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>
        </button>
      </div>
      <button type="submit" class="team-page__add-to-cart button">Add to Cart</button>
    </form>`;
}

export class TeamPage {
  constructor() {
    this.config = this.#loadConfig();
    this.activeProduct = null;
    this.basePrice = 0;
    this.productContainer = document.getElementById('team-product-container');
    this.zoomDialog = document.getElementById('team-zoom-dialog');
    if (this.zoomDialog) {
      this.zoomDialog.addEventListener('click', (event) => {
        if (event.target === this.zoomDialog || event.target.closest('.team-page__zoom-close')) {
          this.zoomDialog.close();
        }
      });
    }

    this.#listenForQuantityButtons();
    this.#listenForAddToCart();
    this.#renderProductButtons();
    this.#observeColorChanges();
  }

  #loadConfig() {
    const element = document.getElementById('team-page-config');
    if (!element) return {};

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('Failed to parse team page config:', error);
      return {};
    }
  }

  #renderProductButtons() {
    const container = document.getElementById('team-product-buttons');
    const availableProducts = filterAllowedProducts(
      this.config.productTypes || [],
      this.config.productMap || {},
    );

    if (availableProducts.length === 0) {
      container.innerHTML = '<p>No products available for this team.</p>';
      return;
    }

    container.innerHTML = availableProducts.map((product) =>
      `<button type="button" class="team-page__product-button" data-product-key="${this.#escapeHtml(product.key)}">${this.#escapeHtml(product.title)}</button>`
    ).join('');

    container.addEventListener('click', (event) => {
      const button = event.target.closest('.team-page__product-button');
      if (!button) return;

      const key = button.dataset.productKey;
      this.#selectProduct(key, button);
    });

    const params = new URLSearchParams(window.location.search);
    const preselect = params.get('product');
    const initial = (preselect && availableProducts.find((p) => p.key === preselect)) || availableProducts[0];
    const initialButton = container.querySelector(`[data-product-key="${initial.key}"]`);
    if (initialButton) {
      this.#selectProduct(initial.key, initialButton);
    }
  }

  async #selectProduct(key, button) {
    if (this.activeProduct === key) return;
    this.activeProduct = key;

    // Update button states
    const allButtons = document.querySelectorAll('.team-page__product-button');
    allButtons.forEach((btn) => btn.classList.remove('team-page__product-button--active'));
    button.classList.add('team-page__product-button--active');

    // Show loading state
    this.productContainer.innerHTML = '<div class="team-page__loading">Loading product options...</div>';

    const product = this.config.productMap[key];
    if (!product) return;

    try {
      const response = await fetch(`/products/${product.handle}.json`);
      if (!response.ok) throw new Error('Failed to load product');

      const { product: productData } = await response.json();
      const variant = findVariantByTeamSlug(productData.options, productData.variants, this.config.teamSlug);

      this.basePrice = parseFloat(variant.price);
      this.#updatePrice(this.basePrice);
      this.productContainer.innerHTML = this.#buildProductForm(productData, variant);

      this.#injectWeightField(key);

      if (window.GPOConfigs?.loadMainScripts) {
        const form = this.productContainer.querySelector('form[action*="/cart/add"]');
        if (form) {
          GPOConfigs.loadMainScripts(productData, GPOConfigs.customer, form);
        }
      }

      // Watch for Globo fields to appear, then hide locked ones
      this.#observeAndHideLockedFields();
      this.#observeAddonPriceChanges();

      this.#showStickImage(this.#getGloboColor());
    } catch (error) {
      this.productContainer.innerHTML = `<p>Failed to load product options. Please refresh and try again.</p>`;
    }
  }

  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  #buildProductForm(productData, variant) {
    return buildProductFormHtml(variant, {
      teamSlug: this.config.teamSlug || '',
      teamUrl: window.location.pathname,
    });
  }

  #getStickImages() {
    const goalieImages = this.config.goalieStickImages || {};
    if (this.activeProduct === 'goalie' && Object.keys(goalieImages).length > 0) {
      return goalieImages;
    }
    return this.config.stickImages || {};
  }

  #getGloboColor() {
    const lockedColor = this.config.lockedOptions?.color;
    if (lockedColor?.length === 1) return lockedColor[0];

    const labels = this.productContainer.querySelectorAll('label, .globo-option-label, [data-option-label]');
    for (const label of labels) {
      const labelText = label.textContent.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      if (labelText.includes('color')) {
        const wrapper = label.closest('.globo-option, .globo-product-option, [data-option-index], fieldset') || label.parentElement;
        if (wrapper) {
          const checked = wrapper.querySelector('input:checked');
          if (checked) return checked.value;
        }
      }
    }

    const stickImages = this.#getStickImages();
    const colors = Object.keys(stickImages);
    return colors[0] || null;
  }

  #injectWeightField(productType) {
    const weightValues = resolveWeightForProduct(this.config.lockedOptions?.weight, productType);
    if (weightValues.length !== 1) return;

    const form = this.productContainer.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_Weight]';
    input.value = weightValues[0];
    form.appendChild(input);
  }

  static OPTIONAL_LOCKED_OPTIONS = ['age_group'];

  #hideLockedFields() {
    const lockedOptions = { ...(this.config.lockedOptions || {}) };
    for (const key of TeamPage.OPTIONAL_LOCKED_OPTIONS) {
      if (!(key in lockedOptions)) lockedOptions[key] = [];
    }

    for (const [key, allowedValues] of Object.entries(lockedOptions)) {
      let values;
      if (key === 'weight') {
        values = resolveWeightForProduct(allowedValues, this.activeProduct);
        if (values.length === 0) continue;
      } else {
        values = Array.isArray(allowedValues) ? allowedValues : [allowedValues];
      }
      const optionKey = key.toLowerCase().replace(/[_\s-]+/g, '');

      const inputs = this.productContainer.querySelectorAll('input[type="radio"], input[type="checkbox"], input[type="text"], select');
      for (const input of inputs) {
        const inputName = (input.getAttribute('data-field-name') || input.name || '').toLowerCase().replace(/[_\s-]+/g, '');
        if (inputName !== optionKey) continue;

        const wrapper = input.closest('.globo-option, .globo-product-option, .gpo-form__group, [data-option-index], fieldset') || input.parentElement;
        if (!wrapper) break;

        if (values.length <= 1) {
          if (values.length === 1) this.#selectLockedValue(wrapper, values[0]);
          wrapper.style.display = 'none';
        } else {
          const choices = wrapper.querySelectorAll('input[type="radio"], input[type="checkbox"]');
          choices.forEach((choice) => {
            const choiceWrapper = choice.closest('.globo-option-value, .globo-swatch, [data-option-value]') || choice.parentElement;
            if (!choiceWrapper) return;
            if (values.includes(choice.value)) return;
            choiceWrapper.style.display = 'none';
          });
        }
        break;
      }
    }
  }
  #selectLockedValue(wrapper, value) {
    const choice = [...wrapper.querySelectorAll('input[type="radio"], input[type="checkbox"]')]
      .find((c) => c.value === value);
    if (choice) {
      if (!choice.checked) choice.click();
      return;
    }
    const field = wrapper.querySelector('select, input[type="text"]');
    if (field && field.value !== value) {
      field.value = value;
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  #isHidden(element, boundary) {
    let current = element;
    while (current && current !== boundary) {
      if (current.style.display === 'none') return true;
      current = current.parentElement;
    }
    return false;
  }

  #validateVisibleFields(form) {
    const required = [...form.querySelectorAll('[required], [presence]')];
    const emptyVisible = required.find((el) => {
      if (this.#isHidden(el, form)) return false;
      if (el.type === 'radio') {
        return !form.querySelector(`input[type="radio"][name="${el.name}"]:checked`);
      }
      return !el.value;
    });

    if (emptyVisible) {
      emptyVisible.reportValidity();
      return false;
    }
    return true;
  }

  #listenForQuantityButtons() {
    this.productContainer.addEventListener('click', (event) => {
      const button = event.target.closest('button[name="minus"], button[name="plus"]');
      if (!button) return;

      const input = this.productContainer.querySelector('input[name="quantity"]');
      if (!input) return;

      const current = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;

      if (button.name === 'plus') {
        input.value = current + 1;
      } else if (button.name === 'minus' && current > min) {
        input.value = current - 1;
      }
    });
  }

  #listenForAddToCart() {
    this.productContainer.addEventListener('click', async (event) => {
      const button = event.target.closest('.team-page__add-to-cart');
      if (!button) return;
      event.preventDefault();

      const form = this.productContainer.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      if (!this.#validateVisibleFields(form)) return;

      const product = this.config.productMap[this.activeProduct];
      if (!product) return;
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding to cart...';
      }

      try {
        const formData = new FormData(form);
        const properties = buildCartProperties(
          Array.from(formData.entries()),
          this.config.lockedOptions || {},
          this.config.teamSlug || '',
          this.activeProduct,
        );

        if (this.config.suppressUpcharges) {
          delete properties._gpo_addon_products;
        }

        const variantId = formData.get('id') || product.variantId;
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        const response = await fetch('/cart/add.js', {
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              properties: properties,
              quantity: quantity
            }]
          }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });

        if (!response.ok) {
          const responseData = await response.json();
          throw new Error(responseData.description || 'Failed to add to cart');
        }

        const discountCode = this.config.discountCode;
        if (discountCode) {
          window.location.href = `/discount/${encodeURIComponent(discountCode)}?redirect=/cart`;
        } else {
          window.location.href = '/cart';
        }
      } catch (error) {
        if (button) {
          button.disabled = false;
          button.textContent = 'Add to Cart';
        }
        this.#showCartError(error.message);
      }
    });
  }

  #observeAndHideLockedFields() {
    const handler = () => {
      this.#hideLockedFields();
      const color = this.#getGloboColor();
      this.#showStickImage(color);
      this.#updateStickImageField(color);
    };

    document.addEventListener('gpoRenderCompleted', handler, { once: true });

    // Fallback in case the event never fires
    setTimeout(() => {
      document.removeEventListener('gpoRenderCompleted', handler);
      handler();
    }, 5000);
  }

  #observeColorChanges() {
    this.productContainer.addEventListener('change', (event) => {
      const input = event.target;
      if (input.type !== 'radio') return;
      if (input.name.toLowerCase() !== 'color') return;

      const stickImages = this.#getStickImages();
      if (stickImages[input.value]) {
        this.#showStickImage(input.value);
        this.#updateStickImageField(input.value);
      }
    });
  }

  #openZoom(url, alt) {
    if (!this.zoomDialog) return;

    const image = this.zoomDialog.querySelector('.team-page__zoom-image');
    image.src = url;
    image.alt = alt;
    this.zoomDialog.showModal();
  }

  #showCartError(message) {
    const existing = this.productContainer.querySelector('.team-page__cart-error');
    if (existing) existing.remove();

    const errorDiv = document.createElement('div');
    errorDiv.className = 'team-page__cart-error';
    errorDiv.textContent = `Failed to add to cart: ${message}`;
    this.productContainer.appendChild(errorDiv);
  }

  #showStickImage(color) {
    const stickImages = this.#getStickImages();
    if (Object.keys(stickImages).length === 0) return;

    const url = stickImages[color];
    if (!url) return;

    const imageSlot = document.getElementById('team-stick-image-slot');
    let container = imageSlot?.querySelector('.team-page__stick-image');
    if (container) {
      container.querySelector('img').src = url;
      container.querySelector('img').alt = `${this.#escapeHtml(this.config.teamName || '')} - ${this.#escapeHtml(color)}`;
      return;
    }

    container = document.createElement('div');
    container.className = 'team-page__stick-image';
    container.innerHTML = `<img
      src="${this.#escapeHtml(url)}"
      alt="${this.#escapeHtml(this.config.teamName || '')} - ${this.#escapeHtml(color)}"
      loading="eager"
      style="width: 100%; height: auto; object-fit: contain; cursor: zoom-in;">`;

    container.querySelector('img').addEventListener('click', () => {
      this.#openZoom(container.querySelector('img').src, container.querySelector('img').alt);
    });

    if (imageSlot) {
      imageSlot.appendChild(container);
    }
  }

  #updatePrice(price) {
    const element = document.querySelector('.team-page__product-price');
    if (element) element.textContent = `$${parseFloat(price).toFixed(2)}`;
  }

  #recalculatePrice() {
    const addonInput = this.productContainer.querySelector('input[name="properties[_gpo_addon_products]"]');
    let addonTotal = 0;
    if (addonInput?.value) {
      try {
        const addons = JSON.parse(addonInput.value);
        addonTotal = addons.reduce((sum, addon) => sum + (parseFloat(addon.p) || 0) * (parseInt(addon.q, 10) || 1), 0);
      } catch { /* ignore malformed JSON */ }
    }
    this.#updatePrice(this.basePrice + addonTotal);
  }

  #observeAddonPriceChanges() {
    if (this.config.suppressUpcharges) return;

    const form = this.productContainer.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const observer = new MutationObserver(() => this.#recalculatePrice());
    observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });

    this.productContainer.addEventListener('change', () => {
      setTimeout(() => this.#recalculatePrice(), 100);
    });
  }

  #updateStickImageField(color) {
    const url = this.#getStickImages()[color] || '';
    const field = this.productContainer.querySelector('input[name="properties[_stick_image]"]');
    if (field) field.value = url;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('team-page-config')) {
    new TeamPage();
  }
});
